/**
 * Poloha hotelu na mapě (vize C13) — JEDINÉ místo, které rozhoduje, jestli hotel souřadnice má.
 *
 * Čtou ho web (vykreslit sekci „Poloha hotelu"?) i admin (indikátor „Bez souřadnic" ve výpisu,
 * výchozí text pole v záložce Hotel). Kdyby si každá vrstva testovala `hotelLatitude != null`
 * sama, rozešly by se na `NaN`, na jedné vyplněné hodnotě nebo na hodnotě mimo rozsah — a admin
 * by hlásil „má souřadnice" u hotelu, kterému web mapu nevykreslí.
 */

export const LATITUDE_RANGE = { min: -90, max: 90 } as const;
export const LONGITUDE_RANGE = { min: -180, max: 180 } as const;

export interface CoordinatePair {
  lat: number;
  lng: number;
}

const inRange = (value: number, range: { min: number; max: number }) =>
  value >= range.min && value <= range.max;

/** Obě hodnoty konečná čísla v rozsahu → dvojice; cokoli jiného → `null`. */
export function hotelCoordinates(trip: {
  hotelLatitude?: number | null;
  hotelLongitude?: number | null;
}): CoordinatePair | null {
  const lat = trip.hotelLatitude;
  const lng = trip.hotelLongitude;
  if (typeof lat !== "number" || typeof lng !== "number") return null;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (!inRange(lat, LATITUDE_RANGE) || !inRange(lng, LONGITUDE_RANGE)) return null;
  return { lat, lng };
}

export type CoordinateInputResult =
  | { ok: true; value: CoordinatePair | null }
  | { ok: false; reason: "format" | "range" };

/**
 * Dvě čísla s desetinnou TEČKOU oddělená čárkou nebo středníkem, mezery volitelné. Čte oba tvary,
 * které kopírují mapy: Mapy Google dávají znaménko („-8.79203, 115.22704"), Mapy.cz světové
 * strany („8.79203S, 115.22704E"). Strana je u čísla bez znaménka; S a W znamenají zápornou
 * hodnotu. Kombinace znaménka se stranou nebo strana jen u jedné hodnoty je chyba formátu.
 */
const COORDINATE_PAIR_PATTERN =
  /^\s*(-?\d+(?:\.\d+)?)\s*([NS])?\s*[,;]\s*(-?\d+(?:\.\d+)?)\s*([EW])?\s*$/i;

/** Hodnota s volitelnou světovou stranou; `null` = znaménko i strana zároveň. */
function signedValue(raw: string, hemisphere: string | undefined): number | null {
  const value = Number(raw);
  if (!hemisphere) return value;
  if (raw.startsWith("-")) return null;
  const negative = hemisphere.toUpperCase() === "S" || hemisphere.toUpperCase() === "W";
  return negative ? -value : value;
}

/**
 * „8,79" je číslo s desetinnou čárkou, ne dvojice 8 a 79: čárka těsně mezi dvěma celými
 * čísly bez tečky je nejednoznačná, a tichý odhad by hotel posadil jinam. Je to chyba
 * formátu. „95, 10" (s mezerou) nebo „-8.79,115.2" (s tečkou) jsou dvojice.
 */
const DECIMAL_COMMA_PATTERN = /^\s*-?\d+,\d+\s*$/;

/** Přečte text pole adminu. Prázdný vstup = vymazání (`value: null`). */
export function parseCoordinateInput(text: string | null | undefined): CoordinateInputResult {
  if (!text || text.trim() === "") return { ok: true, value: null };
  const match = COORDINATE_PAIR_PATTERN.exec(text);
  if (!match || DECIMAL_COMMA_PATTERN.test(text)) return { ok: false, reason: "format" };
  const [, rawLat, latSide, rawLng, lngSide] = match;
  if (Boolean(latSide) !== Boolean(lngSide)) return { ok: false, reason: "format" };
  const lat = signedValue(rawLat, latSide);
  const lng = signedValue(rawLng, lngSide);
  if (lat === null || lng === null) return { ok: false, reason: "format" };
  const pair = hotelCoordinates({ hotelLatitude: lat, hotelLongitude: lng });
  if (!pair) return { ok: false, reason: "range" };
  return { ok: true, value: pair };
}

/** Opak `parseCoordinateInput`: „-8.79203, 115.22704", bez dvojice prázdný řetězec. */
export function formatCoordinateInput(pair: CoordinatePair | null): string {
  return pair ? `${pair.lat}, ${pair.lng}` : "";
}
